import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';

function SessionsManager({ programId }) {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    start_time: '',
    end_time: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchSessions();
  }, [programId]);

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await api.getSessions(programId);
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError(error.response?.data?.error || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (session = null) => {
    if (session) {
      // Edit mode
      setEditingSession(session);
      setFormData({
        name: session.name || '',
        description: session.description || '',
        location: session.location || '',
        start_time: session.start_time ? new Date(session.start_time).toISOString().slice(0, 16) : '',
        end_time: session.end_time ? new Date(session.end_time).toISOString().slice(0, 16) : ''
      });
    } else {
      // Create mode
      setEditingSession(null);
      setFormData({
        name: '',
        description: '',
        location: '',
        start_time: '',
        end_time: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSession(null);
    setFormData({
      name: '',
      description: '',
      location: '',
      start_time: '',
      end_time: ''
    });
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Session name is required';
    if (!formData.start_time) errors.start_time = 'Start time is required';
    if (!formData.end_time) errors.end_time = 'End time is required';

    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time);
      const end = new Date(formData.end_time);
      if (end <= start) {
        errors.end_time = 'End time must be after start time';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const sessionData = {
        program_id: programId,
        name: formData.name,
        description: formData.description,
        location: formData.location,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString()
      };

      if (editingSession) {
        await api.updateSession(programId, editingSession.id, sessionData);
      } else {
        await api.createSession(programId, sessionData);
      }

      await fetchSessions();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving session:', error);
      setFormErrors({ submit: error.response?.data?.error || 'Failed to save session' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (sessionId, sessionName) => {
    if (!window.confirm(`Are you sure you want to delete "${sessionName}"?`)) {
      return;
    }

    try {
      await api.deleteSession(programId, sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (error) {
      console.error('Error deleting session:', error);
      alert(error.response?.data?.error || 'Failed to delete session');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Sessions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Session
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No sessions yet. Create your first session to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Start Time</strong></TableCell>
                <TableCell><strong>End Time</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id} hover>
                  <TableCell>
                    <Typography variant="body1">{session.name}</Typography>
                    {session.description && (
                      <Typography variant="caption" color="text.secondary">
                        {session.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{session.location || '—'}</TableCell>
                  <TableCell>{formatDateTime(session.start_time)}</TableCell>
                  <TableCell>{formatDateTime(session.end_time)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(session)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(session.id, session.name)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSession ? 'Edit Session' : 'Create New Session'}
        </DialogTitle>
        <DialogContent>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>{formErrors.submit}</Alert>
          )}
          <TextField
            fullWidth
            label="Session Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Start Time"
            name="start_time"
            type="datetime-local"
            value={formData.start_time}
            onChange={handleChange}
            error={!!formErrors.start_time}
            helperText={formErrors.start_time}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="End Time"
            name="end_time"
            type="datetime-local"
            value={formData.end_time}
            onChange={handleChange}
            error={!!formErrors.end_time}
            helperText={formErrors.end_time}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : (editingSession ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SessionsManager;
